import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
  domtools,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-diff.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { renderMarkdown } from './dotglobal-markdown.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TPullFilter = 'open' | 'closed' | 'merged' | 'all';
type TReviewState = Exclude<interfaces.TRepositoryPullRequestReviewState, 'dismissed'>;

interface IInlineReviewDraft {
  body: string;
  line: string;
  path: string;
  side: interfaces.TRepositoryPullRequestReviewSide;
}

interface IDiffCommentAnchor {
  line: number;
  path: string;
  side: interfaces.TRepositoryPullRequestReviewSide;
}

@customElement('dotglobal-pulls')
export class DotglobalPulls extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicRepo['response'] | null = null;

  @property({ type: Number })
  public accessor selectedPullRequestNumber = 0;

  @state()
  private accessor pullFilter: TPullFilter = 'open';

  @state()
  private accessor accessToken = '';

  @state()
  private accessor newPullOpen = false;

  @state()
  private accessor newPullTitle = '';

  @state()
  private accessor newPullBody = '';

  @state()
  private accessor newPullSourceRef = '';

  @state()
  private accessor newPullTargetRef = '';

  @state()
  private accessor newPullDraft = false;

  @state()
  private accessor commentBody = '';

  @state()
  private accessor reviewBody = '';

  @state()
  private accessor reviewState: TReviewState = 'commented';

  @state()
  private accessor inlineReviewDrafts: IInlineReviewDraft[] = [];

  @state()
  private accessor mergeMethod: interfaces.TRepositoryPullRequestMergeMethod = 'merge';

  @state()
  private accessor actionPending = false;

  @state()
  private accessor actionError = '';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }
      .notice {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 18px;
        border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border-default));
        border-radius: 8px;
        background: color-mix(in srgb, var(--accent) 7%, var(--bg-primary));
      }
      .listcard {
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        overflow: hidden;
      }
      .listhead {
        min-height: 48px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 16px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
      }
      .listrow {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 13px 16px;
        border-bottom: 1px solid var(--border-subtle);
        color: inherit;
      }
      .listrow:last-child { border-bottom: 0; }
      .listrow:hover { background: var(--row-hover); }
      .listrow .it { flex-shrink: 0; margin-top: 2px; }
      .listrow .ti { color: var(--text-primary); font-size: 14.5px; font-weight: 600; }
      .listrow:hover .ti { color: var(--accent); }
      .listrow .sub { margin-top: 4px; color: var(--text-muted); font-size: 12px; }
      .fpill {
        height: 30px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 0 10px;
        border-radius: 6px;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
      }
      .fpill:hover { color: var(--text-primary); background: var(--hover); }
      .fpill.on { color: var(--text-primary); }
      .actions {
        padding: 16px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
      }
      .action-error { color: var(--error); font-size: 12px; overflow-wrap: anywhere; }
      .branch {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 3px 7px;
        border-radius: 5px;
        background: var(--bg-tertiary);
        color: var(--accent);
        font-family: var(--font-mono);
        font-size: 11.5px;
      }
      .timeline { position: relative; }
      .tl-item { position: relative; display: flex; align-items: flex-start; gap: 14px; }
      .tl-item + .tl-item { margin-top: 16px; }
      .tl-item::after {
        content: '';
        position: absolute;
        left: 15px;
        top: 34px;
        bottom: -16px;
        width: 2px;
        background: var(--border-default);
      }
      .tl-item:last-child::after { display: none; }
      .tl-dot { position: relative; z-index: 1; flex-shrink: 0; box-shadow: 0 0 0 4px var(--bg-primary); border-radius: 50%; }
      .tl-dot .avatar { width: 30px; height: 30px; }
      .thread-item { flex: 1; min-width: 0; overflow: hidden; border: 1px solid var(--border-default); border-radius: 8px; }
      .thread-head {
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 10px 14px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
        flex-wrap: wrap;
      }
      .thread-body { padding: 16px; }
      .inline-comment { margin-top: 12px; border-left: 3px solid var(--accent); padding: 8px 10px; background: var(--bg-secondary); border-radius: 0 5px 5px 0; }
      .inline-draft { padding: 12px; border: 1px solid var(--border-default); border-radius: 6px; background: var(--bg-secondary); }
      .status-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
      .status-item { padding: 10px 12px; border: 1px solid var(--border-default); border-radius: 6px; background: var(--bg-secondary); }
      .status-item b { display: block; font-size: 13px; margin-top: 3px; }
      .fld select {
        width: 100%;
        min-height: 35px;
        padding: 7px 30px 7px 10px;
        font: inherit;
        font-size: 13px;
        color: var(--text-primary);
        background: var(--bg-tertiary);
        border: 1px solid var(--border-default);
        border-radius: 6px;
      }
      .check { display: inline-flex; align-items: center; gap: 8px; color: var(--text-secondary); font-size: 13px; cursor: pointer; }
      .check input { accent-color: var(--accent); }
      .sha { font-family: var(--font-mono); font-size: 11.5px; }
      @media (max-width: 820px) {
        .detail-grid { flex-direction: column; }
        .detail-side { width: 100% !important; }
        .status-grid { grid-template-columns: 1fr; }
        .form-row { flex-direction: column; align-items: stretch !important; }
      }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    const repo = data?.repo;
    if (!data || !repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    if (this.selectedPullRequestNumber) {
      const pullRequest = data.selectedPullRequest
        ?? data.pullRequests.find((candidate) => candidate.number === this.selectedPullRequestNumber);
      if (!pullRequest) {
        return html`
          <div class="page">
            <button class="btn sm ghost" style="margin-bottom:14px" @click=${() => this.navigate(this.pullsHash(repo))}>${this.icon('ChevronLeft')} Back to pull requests</button>
            <div class="empty">Pull request #${this.selectedPullRequestNumber} was not found.</div>
          </div>
        `;
      }
      return this.renderDetail(data, pullRequest);
    }
    return this.renderList(data);
  }

  private renderList(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    const repo = dataArg.repo!;
    const counts = {
      open: dataArg.pullRequests.filter((pullRequest) => pullRequest.state === 'open').length,
      closed: dataArg.pullRequests.filter((pullRequest) => pullRequest.state === 'closed').length,
      merged: dataArg.pullRequests.filter((pullRequest) => pullRequest.state === 'merged').length,
    };
    const visiblePulls = dataArg.pullRequests.filter((pullRequest) =>
      this.pullFilter === 'all' || pullRequest.state === this.pullFilter
    );
    return html`
      <div class="page">
        <div class="notice" style="margin-bottom:18px">
          <div class="agent-glyph">${this.icon('GitPullRequest')}</div>
          <div class="grow">
            <div style="font-weight:600;font-size:13.5px">Review and integrate repository changes</div>
            <div class="muted" style="font-size:12.5px;margin-top:2px">Reviews are bound to the current head commit; merges run through the durable queue.</div>
          </div>
          <button class="btn sm primary" @click=${() => { this.newPullOpen = !this.newPullOpen; this.actionError = ''; }}>${this.icon(this.newPullOpen ? 'X' : 'Plus')} ${this.newPullOpen ? 'Cancel' : 'New pull request'}</button>
        </div>
        ${this.newPullOpen ? this.renderCreateForm(repo) : ''}
        <div class="listcard">
          <div class="listhead">
            ${this.renderFilter('open', counts.open, 'GitPullRequest')}
            ${this.renderFilter('merged', counts.merged, 'GitMerge')}
            ${this.renderFilter('closed', counts.closed, 'CircleX')}
            <span class="grow"></span>
            <button class=${`fpill ${this.pullFilter === 'all' ? 'on' : ''}`} @click=${() => { this.pullFilter = 'all'; }}>All ${dataArg.pullRequests.length}</button>
          </div>
          ${visiblePulls.length
            ? visiblePulls.map((pullRequest) => this.renderRow(repo, pullRequest))
            : html`<div class="empty">No pull requests match this filter.</div>`}
        </div>
      </div>
    `;
  }

  private renderFilter(filterArg: Exclude<TPullFilter, 'all'>, countArg: number, iconArg: string): TemplateResult {
    return html`<button class=${`fpill ${this.pullFilter === filterArg ? 'on' : ''}`} @click=${() => { this.pullFilter = filterArg; }}>${this.icon(iconArg)} ${countArg} ${this.capitalize(filterArg)}</button>`;
  }

  private renderCreateForm(repoArg: interfaces.IPublicRepo): TemplateResult {
    return html`
      <div class="actions stack" style="gap:12px;margin-bottom:18px">
        <div class="row form-row" style="gap:12px;align-items:flex-start">
          <div class="fld">
            <label>Source branch</label>
            <input class="mono" maxlength="255" placeholder="feature/my-change" .value=${this.newPullSourceRef} @input=${(eventArg: Event) => { this.newPullSourceRef = (eventArg.currentTarget as HTMLInputElement).value; }} />
          </div>
          <div class="fld">
            <label>Target branch</label>
            <input class="mono" maxlength="255" .value=${this.newPullTargetRef || repoArg.defaultBranch} @input=${(eventArg: Event) => { this.newPullTargetRef = (eventArg.currentTarget as HTMLInputElement).value; }} />
          </div>
        </div>
        <div class="fld">
          <label>Title</label>
          <input maxlength="256" placeholder="Describe the proposed change" .value=${this.newPullTitle} @input=${(eventArg: Event) => { this.newPullTitle = (eventArg.currentTarget as HTMLInputElement).value; }} />
        </div>
        <div class="fld">
          <label>Description</label>
          <textarea rows="6" maxlength="65536" placeholder="Explain the change, its motivation, and how it was tested." .value=${this.newPullBody} @input=${(eventArg: Event) => { this.newPullBody = (eventArg.currentTarget as HTMLTextAreaElement).value; }}></textarea>
        </div>
        <div class="row form-row" style="gap:12px;align-items:flex-end">
          <div class="fld grow">
            <label>Organization access token</label>
            <input type="password" autocomplete="off" placeholder="fgu_…" .value=${this.accessToken} @input=${(eventArg: Event) => { this.accessToken = (eventArg.currentTarget as HTMLInputElement).value; }} />
          </div>
          <label class="check"><input type="checkbox" .checked=${this.newPullDraft} @change=${(eventArg: Event) => { this.newPullDraft = (eventArg.currentTarget as HTMLInputElement).checked; }} /> Draft</label>
          <button class="btn sm primary" ?disabled=${this.actionPending || !this.newPullTitle.trim() || !this.newPullSourceRef.trim() || this.accessToken.length < 32} @click=${() => this.createPullRequest(repoArg)}>${this.icon('GitPullRequestCreate')} ${this.actionPending ? 'Creating…' : 'Create pull request'}</button>
        </div>
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
      </div>
    `;
  }

  private renderDetail(
    dataArg: interfaces.IReq_PublicRepo['response'],
    pullRequestArg: interfaces.IPublicPullRequest,
  ): TemplateResult {
    const repo = dataArg.repo!;
    const stateColor = pullRequestArg.state === 'merged' ? '#8b5cf6' : pullRequestArg.state === 'open' ? 'var(--success)' : 'var(--text-muted)';
    return html`
      <div class="page">
        <button class="btn sm ghost" style="margin-bottom:14px" @click=${() => this.navigate(this.pullsHash(repo))}>${this.icon('ChevronLeft')} Back to pull requests</button>
        <div style="border-bottom:1px solid var(--border-default);padding-bottom:16px;margin-bottom:20px">
          <h1 style="margin:0;font-size:24px;line-height:1.2;letter-spacing:-0.02em">${pullRequestArg.title} <span class="muted" style="font-weight:400">#${pullRequestArg.number}</span></h1>
          <div class="row wrap" style="gap:9px;margin-top:12px">
            <span class="badge sq" style=${`padding:5px 12px;font-size:12.5px;background:${stateColor};color:#fff`}>${this.icon(this.stateIcon(pullRequestArg.state))} ${this.capitalize(pullRequestArg.state)}</span>
            ${pullRequestArg.draft ? html`<span class="badge warning sq">Draft</span>` : ''}
            <span class="muted" style="font-size:13px"><b style="color:var(--text-primary)">${pullRequestArg.user.login || 'unknown'}</b> wants to merge</span>
            <span class="branch">${pullRequestArg.sourceRef}</span>
            <span class="muted">into</span>
            <span class="branch">${pullRequestArg.targetRef}</span>
          </div>
        </div>

        <div class="row detail-grid" style="align-items:flex-start;gap:24px">
          <div class="grow stack" style="gap:18px;min-width:0">
            <div class="timeline">
              ${this.renderConversationEntry(pullRequestArg.user, pullRequestArg.createdAt, pullRequestArg.body || 'No pull request description provided.', 'opened this pull request', html`<span class="badge outline sq">author</span>`)}
              ${this.renderConversation(dataArg)}
            </div>
            ${this.renderFilesChanged(dataArg, pullRequestArg)}
            ${pullRequestArg.state === 'open' ? this.renderCommentForm(repo, pullRequestArg) : ''}
            ${pullRequestArg.state === 'open' ? this.renderReviewForm(repo, pullRequestArg) : ''}
          </div>
          <aside class="detail-side stack" style="width:270px;flex-shrink:0;gap:14px">
            ${this.renderStatusCard(pullRequestArg)}
            ${this.renderManagementCard(repo, pullRequestArg)}
          </aside>
        </div>
      </div>
    `;
  }

  private renderConversation(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult[] {
    const repo = dataArg.repo!;
    const pullRequest = dataArg.selectedPullRequest!;
    const entries = [
      ...dataArg.selectedPullRequestComments.map((comment) => ({ kind: 'comment' as const, timestamp: comment.createdAt, comment })),
      ...dataArg.selectedPullRequestReviews.map((review) => ({ kind: 'review' as const, timestamp: review.submittedAt, review })),
    ].sort((left, right) => left.timestamp.localeCompare(right.timestamp));
    return entries.map((entry) => {
      if (entry.kind === 'comment') {
        return this.renderConversationEntry(entry.comment.user, entry.comment.createdAt, entry.comment.body, 'commented');
      }
      const review = entry.review;
      const reviewComments = dataArg.selectedPullRequestReviewComments.filter((comment) => comment.reviewId === review.id);
      return this.renderConversationEntry(
        review.user,
        review.submittedAt,
        review.body || this.reviewLabel(review.state),
        this.reviewLabel(review.state),
        html`
          <span class=${`badge sq ${this.reviewBadgeClass(review.state)}`}>${this.icon(this.reviewIcon(review.state))} ${this.reviewLabel(review.state)}</span>
          ${review.state !== 'dismissed' && pullRequest.state === 'open'
            ? html`<button class="btn sm ghost" ?disabled=${this.actionPending || this.accessToken.length < 32} @click=${() => this.dismissReview(repo, pullRequest, review)}>${this.icon('Ban')} Dismiss</button>`
            : ''}
        `,
        reviewComments,
      );
    });
  }

  private renderConversationEntry(
    userArg: interfaces.IPublicUser,
    timestampArg: string,
    bodyArg: string,
    actionArg: string,
    badgeArg: TemplateResult | string = '',
    inlineCommentsArg: interfaces.IPublicPullRequestReviewComment[] = [],
  ): TemplateResult {
    return html`
      <div class="tl-item">
        <div class="tl-dot">${this.renderAvatar(userArg.login || 'unknown', userArg.avatarUrl)}</div>
        <div class="thread-item">
          <div class="thread-head">
            <b>${userArg.login || 'unknown'}</b>
            <span class="muted" style="font-size:12px">${actionArg} ${this.timeAgo(timestampArg)}</span>
            <span class="grow"></span>${badgeArg}
          </div>
          <div class="thread-body">
            <div class="md">${renderMarkdown(bodyArg, 120)}</div>
            ${inlineCommentsArg.map((comment) => html`
              <div class="inline-comment">
                <div class="row wrap" style="gap:7px;font-size:11.5px">
                  <span class="branch">${comment.path}:${comment.line}</span>
                  <span class="badge outline sq">${comment.side}</span>
                  ${comment.stale ? html`<span class="badge warning sq">outdated</span>` : ''}
                </div>
                <div class="md" style="margin-top:8px">${renderMarkdown(comment.body, 60)}</div>
              </div>
            `)}
          </div>
        </div>
      </div>
    `;
  }

  private renderCommentForm(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): TemplateResult {
    return html`
      <div class="actions stack" style="gap:10px;margin-left:44px">
        <div class="fld">
          <label>Add comment</label>
          <textarea rows="4" maxlength="65536" placeholder="Join the conversation." .value=${this.commentBody} @input=${(eventArg: Event) => { this.commentBody = (eventArg.currentTarget as HTMLTextAreaElement).value; }}></textarea>
        </div>
        <div class="row form-row" style="gap:10px;align-items:flex-end">
          ${this.renderTokenField()}
          <button class="btn sm primary" ?disabled=${this.actionPending || !this.commentBody.trim() || this.accessToken.length < 32} @click=${() => this.createComment(repoArg, pullRequestArg)}>${this.icon('MessageSquare')} ${this.actionPending ? 'Posting…' : 'Comment'}</button>
        </div>
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
      </div>
    `;
  }

  private renderFilesChanged(
    dataArg: interfaces.IReq_PublicRepo['response'],
    pullRequestArg: interfaces.IPublicPullRequest,
  ): TemplateResult {
    const diff = dataArg.selectedPullRequestDiff;
    return html`
      <section class="stack" style="gap:10px">
        <div class="row wrap" style="gap:8px">
          <h2 style="margin:0;font-size:17px">Files changed</h2>
          ${diff ? html`
            <span class="badge outline sq">${diff.filesChanged} file${diff.filesChanged === 1 ? '' : 's'}</span>
            <span class="badge success sq">+${diff.additions}</span>
            <span class="badge error sq">−${diff.deletions}</span>
          ` : ''}
          <span class="grow"></span>
          ${pullRequestArg.state === 'open' && diff
            ? html`<span class="muted" style="font-size:12px">Select a base or head line number to add an inline comment.</span>`
            : ''}
        </div>
        ${diff
          ? html`<dotglobal-diff
              .files=${diff.files}
              .truncated=${diff.truncated}
              .reviewEnabled=${pullRequestArg.state === 'open'}
              @dotglobal-diff-comment=${(eventArg: CustomEvent<IDiffCommentAnchor>) => this.addInlineDraft(eventArg.detail)}
            ></dotglobal-diff>`
          : html`<div class="empty actions">The exact base-to-head diff is unavailable for this pull request.</div>`}
      </section>
    `;
  }

  private renderReviewForm(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): TemplateResult {
    return html`
      <div class="actions stack" style="gap:12px;margin-left:44px">
        <div class="row wrap" style="gap:8px">
          <b style="font-size:13.5px">Submit review</b>
          <span class="badge outline sq sha" title=${pullRequestArg.sourceObjectId}>head ${this.shortSha(pullRequestArg.sourceObjectId)}</span>
        </div>
        <div class="fld">
          <label>Summary</label>
          <textarea rows="4" maxlength="65536" placeholder="Leave review feedback." .value=${this.reviewBody} @input=${(eventArg: Event) => { this.reviewBody = (eventArg.currentTarget as HTMLTextAreaElement).value; }}></textarea>
        </div>
        ${this.inlineReviewDrafts.map((draft, index) => this.renderInlineDraft(draft, index))}
        ${this.inlineReviewDrafts.length
          ? ''
          : html`<div class="muted" style="font-size:12px">Use a line number in Files changed to add an inline comment.</div>`}
        <div class="row form-row" style="gap:10px;align-items:flex-end">
          <div class="fld" style="max-width:190px">
            <label>Decision</label>
            <select .value=${this.reviewState} @change=${(eventArg: Event) => { this.reviewState = (eventArg.currentTarget as HTMLSelectElement).value as TReviewState; }}>
              <option value="commented">Comment</option>
              <option value="approved">Approve</option>
              <option value="changes_requested">Request changes</option>
            </select>
          </div>
          ${this.renderTokenField()}
          <button class="btn sm primary" ?disabled=${this.actionPending || (!this.reviewBody.trim() && this.inlineReviewDrafts.length === 0) || this.accessToken.length < 32} @click=${() => this.submitReview(repoArg, pullRequestArg)}>${this.icon('Send')} ${this.actionPending ? 'Submitting…' : 'Submit review'}</button>
        </div>
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
      </div>
    `;
  }

  private renderInlineDraft(draftArg: IInlineReviewDraft, indexArg: number): TemplateResult {
    return html`
      <div class="inline-draft stack" style="gap:9px">
        <div class="row form-row" style="gap:9px;align-items:center">
          <div class="grow row wrap" style="gap:7px">
            <span class="muted" style="font-size:11.5px">Inline anchor</span>
            <span class="branch">${draftArg.path}:${draftArg.line}</span>
            <span class="badge outline sq">${draftArg.side}</span>
          </div>
          <button class="btn sm danger" @click=${() => this.removeInlineDraft(indexArg)} title="Remove inline comment">${this.icon('Trash2')}</button>
        </div>
        <div class="fld"><label>Inline comment</label><textarea rows="3" maxlength="65536" placeholder="Comment on this changed line." .value=${draftArg.body} @input=${(eventArg: Event) => this.updateInlineDraft(indexArg, 'body', (eventArg.currentTarget as HTMLTextAreaElement).value)}></textarea></div>
      </div>
    `;
  }

  private renderStatusCard(pullRequestArg: interfaces.IPublicPullRequest): TemplateResult {
    return html`
      <div class="actions stack" style="gap:11px">
        <div class="row" style="gap:7px"><b style="font-size:13px">Review status</b><span class="grow"></span><button class="btn sm ghost" ?disabled=${this.actionPending} @click=${() => this.refreshDetail(pullRequestArg)} title="Refresh">${this.icon('RefreshCw')}</button></div>
        <div class="status-grid">
          <div class="status-item"><span class="muted" style="font-size:11px">Approvals</span><b>${pullRequestArg.approvals}</b></div>
          <div class="status-item"><span class="muted" style="font-size:11px">Changes requested</span><b>${pullRequestArg.changesRequested}</b></div>
          <div class="status-item"><span class="muted" style="font-size:11px">Comments</span><b>${pullRequestArg.comments}</b></div>
          <div class="status-item"><span class="muted" style="font-size:11px">Merge state</span><b>${this.capitalize(pullRequestArg.mergeState.replace('_', ' '))}</b></div>
        </div>
        ${pullRequestArg.queuePosition !== null ? html`<div class="badge accent sq">${this.icon('ListOrdered')} Queue position ${pullRequestArg.queuePosition}</div>` : ''}
        <div class="muted sha" title=${pullRequestArg.sourceObjectId}>head ${this.shortSha(pullRequestArg.sourceObjectId)}</div>
        <div class="muted sha" title=${pullRequestArg.targetObjectId}>base ${this.shortSha(pullRequestArg.targetObjectId)}</div>
      </div>
    `;
  }

  private renderManagementCard(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): TemplateResult {
    const queued = pullRequestArg.mergeState === 'queued' || pullRequestArg.mergeState === 'merging';
    return html`
      <div class="actions stack" style="gap:10px">
        ${this.renderTokenField()}
        ${pullRequestArg.state === 'open' ? html`
          <div class="fld"><label>Merge method</label><select .value=${this.mergeMethod} @change=${(eventArg: Event) => { this.mergeMethod = (eventArg.currentTarget as HTMLSelectElement).value as interfaces.TRepositoryPullRequestMergeMethod; }}><option value="merge">Merge commit</option><option value="squash">Squash</option><option value="rebase">Rebase</option></select></div>
          ${queued
            ? html`<button class="btn sm danger" ?disabled=${this.actionPending || this.accessToken.length < 32} @click=${() => this.cancelMerge(repoArg, pullRequestArg)}>${this.icon('CircleStop')} Cancel queued merge</button>`
            : html`<button class="btn sm primary" ?disabled=${this.actionPending || this.accessToken.length < 32 || pullRequestArg.draft || pullRequestArg.changesRequested > 0} @click=${() => this.enqueueMerge(repoArg, pullRequestArg)}>${this.icon('GitMerge')} Queue ${this.mergeMethod}</button>`}
          <button class="btn sm" ?disabled=${this.actionPending || this.accessToken.length < 32} @click=${() => this.updateState(repoArg, pullRequestArg, 'open', !pullRequestArg.draft)}>${this.icon(pullRequestArg.draft ? 'BadgeCheck' : 'Construction')} ${pullRequestArg.draft ? 'Mark ready' : 'Convert to draft'}</button>
          <button class="btn sm danger" ?disabled=${this.actionPending || this.accessToken.length < 32 || queued} @click=${() => this.updateState(repoArg, pullRequestArg, 'closed')}>${this.icon('CircleX')} Close pull request</button>
        ` : pullRequestArg.state === 'closed' ? html`
          <button class="btn sm" ?disabled=${this.actionPending || this.accessToken.length < 32} @click=${() => this.updateState(repoArg, pullRequestArg, 'open', pullRequestArg.draft)}>${this.icon('RotateCcw')} Reopen pull request</button>
        ` : html`<div class="badge success sq">${this.icon('GitMerge')} Merged ${pullRequestArg.mergedAt ? this.timeAgo(pullRequestArg.mergedAt) : ''}</div>`}
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
      </div>
    `;
  }

  private renderTokenField(): TemplateResult {
    return html`<div class="fld grow"><label>Organization access token</label><input type="password" autocomplete="off" placeholder="fgu_…" .value=${this.accessToken} @input=${(eventArg: Event) => { this.accessToken = (eventArg.currentTarget as HTMLInputElement).value; }} /></div>`;
  }

  private renderRow(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): TemplateResult {
    const hash = `${this.pullsHash(repoArg)}/${pullRequestArg.number}`;
    return html`
      <a class="listrow" href=${hash} @click=${(eventArg: Event) => { eventArg.preventDefault(); this.navigate(hash); }}>
        <span class="it" style=${`color:${pullRequestArg.state === 'merged' ? '#8b5cf6' : pullRequestArg.state === 'open' ? 'var(--success)' : 'var(--text-muted)'}`}>${this.icon(this.stateIcon(pullRequestArg.state))}</span>
        <div class="grow">
          <div class="row wrap" style="gap:7px"><span class="ti">${pullRequestArg.title}</span>${pullRequestArg.draft ? html`<span class="badge warning sq">Draft</span>` : ''}${pullRequestArg.mergeState === 'queued' || pullRequestArg.mergeState === 'merging' ? html`<span class="badge accent sq">${pullRequestArg.mergeState}</span>` : ''}</div>
          <div class="sub row wrap" style="gap:6px"><span>#${pullRequestArg.number}</span><span>·</span><span>${pullRequestArg.state === 'open' ? 'opened' : pullRequestArg.state} ${this.timeAgo(pullRequestArg.createdAt)}</span><span>·</span><span>by ${pullRequestArg.user.login || 'unknown'}</span><span>·</span><span class="branch">${pullRequestArg.sourceRef}</span><span>→</span><span class="branch">${pullRequestArg.targetRef}</span></div>
        </div>
        <div class="row muted" style="gap:11px;flex-shrink:0;font-size:12px">${pullRequestArg.approvals ? html`<span>${this.icon('Check')} ${pullRequestArg.approvals}</span>` : ''}${pullRequestArg.comments || pullRequestArg.reviews ? html`<span>${this.icon('MessageSquare')} ${pullRequestArg.comments + pullRequestArg.reviews}</span>` : ''}</div>
      </a>
    `;
  }

  private async createPullRequest(repoArg: interfaces.IPublicRepo): Promise<void> {
    if (this.actionPending) return;
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestCreate>('/typedrequest', 'repositoryPullRequestCreate');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, title: this.newPullTitle.trim(), body: this.newPullBody, sourceOrg: repoArg.org, sourceRepo: repoArg.name, sourceRef: this.newPullSourceRef.trim(), targetRef: (this.newPullTargetRef || repoArg.defaultBranch).trim(), draft: this.newPullDraft, accessToken: this.accessToken });
      this.newPullTitle = ''; this.newPullBody = ''; this.newPullSourceRef = ''; this.newPullTargetRef = ''; this.newPullDraft = false; this.newPullOpen = false;
      this.navigate(`${this.pullsHash(repoArg)}/${response.pullRequest.number}`);
    });
  }

  private async createComment(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): Promise<void> {
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestCommentCreate>('/typedrequest', 'repositoryPullRequestCommentCreate');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, body: this.commentBody, accessToken: this.accessToken });
      this.data = this.data ? { ...this.data, selectedPullRequestComments: [...this.data.selectedPullRequestComments, response.comment], pullRequests: this.data.pullRequests.map((candidate) => candidate.number === pullRequestArg.number ? { ...candidate, comments: candidate.comments + 1, updatedAt: response.comment.updatedAt } : candidate), selectedPullRequest: { ...pullRequestArg, comments: pullRequestArg.comments + 1, updatedAt: response.comment.updatedAt } } : this.data;
      this.commentBody = '';
    });
  }

  private async submitReview(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): Promise<void> {
    const comments = this.inlineReviewDrafts.map((draft) => ({ body: draft.body.trim(), path: draft.path.trim(), side: draft.side, line: Number(draft.line) }));
    if (comments.some((comment) => !comment.body || !comment.path || !Number.isSafeInteger(comment.line) || comment.line < 1)) {
      this.actionError = 'Every inline comment needs a repository path, positive line number, and body.';
      return;
    }
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestReviewSubmit>('/typedrequest', 'repositoryPullRequestReviewSubmit');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, state: this.reviewState, body: this.reviewBody, commitId: pullRequestArg.sourceObjectId, comments, accessToken: this.accessToken });
      this.updatePullRequest(response.pullRequest);
      this.data = this.data ? { ...this.data, selectedPullRequestReviews: [...this.data.selectedPullRequestReviews, response.review], selectedPullRequestReviewComments: [...this.data.selectedPullRequestReviewComments, ...response.comments] } : this.data;
      this.reviewBody = ''; this.reviewState = 'commented'; this.inlineReviewDrafts = [];
    });
  }

  private async dismissReview(
    repoArg: interfaces.IPublicRepo,
    pullRequestArg: interfaces.IPublicPullRequest,
    reviewArg: interfaces.IPublicPullRequestReview,
  ): Promise<void> {
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestReviewDismiss>('/typedrequest', 'repositoryPullRequestReviewDismiss');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, reviewId: reviewArg.id, message: 'Dismissed by repository maintainer.', accessToken: this.accessToken });
      this.updatePullRequest(response.pullRequest);
      this.data = this.data ? { ...this.data, selectedPullRequestReviews: this.data.selectedPullRequestReviews.map((candidate) => candidate.id === response.review.id ? response.review : candidate) } : this.data;
    });
  }

  private async updateState(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest, stateArg: 'open' | 'closed', draftArg?: boolean): Promise<void> {
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestStateUpdate>('/typedrequest', 'repositoryPullRequestStateUpdate');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, state: stateArg, draft: draftArg, accessToken: this.accessToken });
      this.updatePullRequest(response.pullRequest);
    });
  }

  private async enqueueMerge(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): Promise<void> {
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestMergeEnqueue>('/typedrequest', 'repositoryPullRequestMergeEnqueue');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, method: this.mergeMethod, expectedSourceObjectId: pullRequestArg.sourceObjectId, expectedTargetObjectId: pullRequestArg.targetObjectId, accessToken: this.accessToken });
      this.updatePullRequest(response.pullRequest);
      globalThis.setTimeout(() => { void this.refreshDetail(response.pullRequest); }, 1500);
    });
  }

  private async cancelMerge(repoArg: interfaces.IPublicRepo, pullRequestArg: interfaces.IPublicPullRequest): Promise<void> {
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_RepositoryPullRequestMergeCancel>('/typedrequest', 'repositoryPullRequestMergeCancel');
      const response = await request.fire({ org: repoArg.org, repo: repoArg.name, pullRequestNumber: pullRequestArg.number, accessToken: this.accessToken });
      this.updatePullRequest(response.pullRequest);
    });
  }

  private async refreshDetail(pullRequestArg: interfaces.IPublicPullRequest): Promise<void> {
    const repo = this.data?.repo;
    if (!repo || this.actionPending) return;
    await this.runAction(async () => {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicRepo>('/typedrequest', 'publicRepo');
      this.data = await request.fire({ org: repo.org, repo: repo.name, pullRequestNumber: pullRequestArg.number });
    });
  }

  private updatePullRequest(pullRequestArg: interfaces.IPublicPullRequest): void {
    this.data = this.data ? { ...this.data, pullRequests: this.data.pullRequests.map((candidate) => candidate.number === pullRequestArg.number ? pullRequestArg : candidate), selectedPullRequest: pullRequestArg } : this.data;
  }

  private async runAction(actionArg: () => Promise<void>): Promise<void> {
    if (this.actionPending) return;
    this.actionPending = true;
    this.actionError = '';
    try { await actionArg(); } catch (error) { this.actionError = this.errorText(error); } finally { this.actionPending = false; }
  }

  private addInlineDraft(anchorArg: IDiffCommentAnchor): void {
    if (this.inlineReviewDrafts.some((draft) =>
      draft.path === anchorArg.path
      && draft.side === anchorArg.side
      && draft.line === String(anchorArg.line)
    )) {
      return;
    }
    this.inlineReviewDrafts = [
      ...this.inlineReviewDrafts,
      {
        body: '',
        line: String(anchorArg.line),
        path: anchorArg.path,
        side: anchorArg.side,
      },
    ];
  }
  private removeInlineDraft(indexArg: number): void { this.inlineReviewDrafts = this.inlineReviewDrafts.filter((_draft, index) => index !== indexArg); }
  private updateInlineDraft(indexArg: number, fieldArg: keyof IInlineReviewDraft, valueArg: string): void { this.inlineReviewDrafts = this.inlineReviewDrafts.map((draft, index) => index === indexArg ? { ...draft, [fieldArg]: valueArg } : draft); }
  private pullsHash(repoArg: interfaces.IPublicRepo): string { return `#/repo/${encodeURIComponent(repoArg.org)}/${encodeURIComponent(repoArg.name)}/pulls`; }
  private navigate(hashArg: string): void { this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true })); }
  private icon(nameArg: string): TemplateResult { return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`; }
  private stateIcon(stateArg: interfaces.TRepositoryPullRequestState): string { return stateArg === 'merged' ? 'GitMerge' : stateArg === 'closed' ? 'CircleX' : 'GitPullRequest'; }
  private reviewIcon(stateArg: interfaces.TRepositoryPullRequestReviewState): string { return stateArg === 'approved' ? 'BadgeCheck' : stateArg === 'changes_requested' ? 'CircleAlert' : stateArg === 'dismissed' ? 'Ban' : 'MessageSquare'; }
  private reviewLabel(stateArg: interfaces.TRepositoryPullRequestReviewState): string { return stateArg === 'approved' ? 'approved these changes' : stateArg === 'changes_requested' ? 'requested changes' : stateArg === 'dismissed' ? 'review dismissed' : 'reviewed'; }
  private reviewBadgeClass(stateArg: interfaces.TRepositoryPullRequestReviewState): string { return stateArg === 'approved' ? 'success' : stateArg === 'changes_requested' ? 'error' : stateArg === 'dismissed' ? 'warning' : 'outline'; }
  private shortSha(shaArg: string): string { return shaArg.slice(0, 8); }
  private capitalize(valueArg: string): string { return valueArg ? `${valueArg[0].toUpperCase()}${valueArg.slice(1)}` : valueArg; }
  private renderAvatar(labelArg: string, avatarUrlArg: string): TemplateResult {
    const initials = (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'fg';
    return html`<span class="avatar">${initials}${avatarUrlArg ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />` : ''}</span>`;
  }
  private timeAgo(timestampArg: string): string {
    if (!timestampArg) return 'recently';
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) return 'recently';
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return days < 60 ? `${days}d ago` : `${Math.round(days / 30)}mo ago`;
  }
  private errorText(errorArg: unknown): string { return errorArg instanceof Error && errorArg.message ? errorArg.message.slice(0, 500) : 'Pull request action failed'; }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-pulls': DotglobalPulls;
  }
}
